function build2DMapDebugSummaryHtml({
  imageData,
  debugCandidates = [],
  cameraSearchSummary = null,
  options = {},
  defaultWidth = 1280,
  defaultHeight = 853,
}) {
  const coarseCandidates = debugCandidates.filter((candidate) => candidate.phase === "coarse" || !candidate.phase);
  const bestCandidate = cameraSearchSummary?.bestCandidate
    || [...coarseCandidates].sort((a, b) => (b.score ?? -Infinity) - (a.score ?? -Infinity))[0]
    || null;

  const searchConfig = cameraSearchSummary?.searchConfig || null;
  const searchConfigRows = searchConfig ? [
    ["Coarse bearings", Number.isFinite(searchConfig.coarseBearingCount)
      ? `${searchConfig.coarseBearingCount} (${formatDebugNumber(searchConfig.coarseBearingStepDegrees, 1)}deg step)`
      : "n/a"],
    ["Padding", Number.isFinite(searchConfig.padding) ? `${searchConfig.padding}px` : "n/a"],
    ["Mapbox style", searchConfig.mapStyleUrl || "n/a"],
    ["Line width", formatDebugInteger(searchConfig.lineWidth)],
    ["Line color", searchConfig.lineColor || "n/a"],
    ["Line opacity", formatDebugNumber(searchConfig.lineOpacity, 2)],
    ["Display size", `${searchConfig.displayWidth || "?"} x ${searchConfig.displayHeight || "?"}`],
  ] : [];

  const scoreWeightRows = [
    ["Area reward", "min(0.78, footprintRatio) x 4200"],
    ["Center penalty", "centerDistanceNormalized x 400"],
    ["Out-of-bounds penalty", "100000 when outsidePaddingAreaRatio > 0"],
  ];

  const metricExplainRows = [
    ["Footprint ratio", "Route bounding-box area relative to the safe (padded) frame area."],
    ["Outside pad area", "Fraction of route bbox area that falls outside the safe padded frame."],
    ["Center offset", "Normalized distance between route bbox center and canvas center."],
    ["On-screen ratio", "Fraction of route points whose projected pixel lies within the canvas."],
    ["Inside-padding ratio", "Fraction of route points whose projected pixel lies within the padded frame."],
    ["Ratios W / H", "Route bbox width and height relative to the safe frame size."],
  ];

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>2D Bearing Search Debug Summary</title>
  <style>
    :root {
      --bg: #f3f6fb;
      --panel: #ffffff;
      --panel-border: rgba(15, 23, 42, 0.08);
      --text: #111827;
      --muted: #4b5563;
      --subtle: #6b7280;
      --accent: #1d4ed8;
      --good: #166534;
      --danger: #b91c1c;
      color-scheme: light;
      font-family: Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    }
    * { box-sizing: border-box; }
    body { margin: 0; background: linear-gradient(180deg, #f5f8fd 0%, #eef3fb 100%); color: var(--text); }
    .page { max-width: 1440px; margin: 0 auto; padding: 28px 20px 40px; display: grid; gap: 20px; }
    .panel { background: var(--panel); border: 1px solid var(--panel-border); border-radius: 20px; box-shadow: 0 18px 45px rgba(15, 23, 42, 0.08); overflow: hidden; }
    .hero { display: grid; grid-template-columns: minmax(0, 1.2fr) minmax(320px, 0.8fr); align-items: stretch; }
    .hero-image-wrap { background: #edf2f7; border-right: 1px solid var(--panel-border); padding: 22px; }
    .hero-image { width: 100%; display: block; border-radius: 14px; border: 1px solid rgba(17, 24, 39, 0.08); background: #fff; }
    .hero-meta { padding: 22px; display: grid; gap: 20px; align-content: start; }
    .eyebrow { font-size: 12px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: var(--subtle); }
    h1, h2, h3, p { margin: 0; }
    h1 { font-size: 28px; line-height: 1.1; }
    .summary-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
    .summary-item { border: 1px solid var(--panel-border); border-radius: 12px; padding: 10px 12px; background: #fbfcff; }
    .summary-label { font-size: 12px; color: var(--subtle); margin-bottom: 4px; }
    .summary-value { font-size: 15px; font-weight: 700; }
    .config-list { display: grid; gap: 8px; }
    .config-row { display: grid; grid-template-columns: 180px minmax(0, 1fr); gap: 12px; font-size: 13px; line-height: 1.4; }
    .config-key { color: var(--subtle); font-weight: 600; }
    .config-value { font-weight: 600; color: var(--text); word-break: break-word; }
    .section { padding: 22px; display: grid; gap: 16px; }
    .explain-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; }
    .explain-card { border: 1px solid var(--panel-border); border-radius: 16px; background: #fbfcff; padding: 16px; display: grid; gap: 12px; }
    .explain-title { font-size: 16px; font-weight: 800; }
    .section-header { display: flex; align-items: baseline; justify-content: space-between; gap: 16px; }
    .section-title { font-size: 22px; font-weight: 800; }
    .section-subtitle { color: var(--muted); font-size: 13px; }
    .card-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(265px, 1fr)); gap: 14px; }
    .candidate-card { border: 1px solid var(--panel-border); border-radius: 14px; overflow: hidden; background: #fff; display: grid; grid-template-rows: auto 1fr; }
    .candidate-card--best { border-color: var(--good); box-shadow: 0 0 0 2px rgba(22, 101, 52, 0.2); }
    .candidate-badge { background: var(--good); color: #fff; font-size: 11px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.05em; padding: 4px 10px; }
    .candidate-image { width: 100%; display: block; background: #eef1f6; border-bottom: 1px solid var(--panel-border); }
    .candidate-image--empty { min-height: 150px; display: grid; place-items: center; color: var(--muted); font-size: 13px; }
    .candidate-body { padding: 12px 14px 14px; display: grid; gap: 6px; font-size: 13px; line-height: 1.35; }
    .candidate-phase { font-size: 13px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.04em; color: var(--accent); }
    .empty-state { padding: 28px; text-align: center; color: var(--muted); font-size: 14px; border: 1px dashed var(--panel-border); border-radius: 14px; background: #fbfcff; }
    @media (max-width: 1100px) {
      .hero { grid-template-columns: 1fr; }
      .hero-image-wrap { border-right: 0; border-bottom: 1px solid var(--panel-border); }
      .summary-grid { grid-template-columns: 1fr; }
      .config-row { grid-template-columns: 1fr; gap: 4px; }
      .explain-grid { grid-template-columns: 1fr; }
    }
  </style>
</head>
<body>
  <div class="page">
    <section class="panel hero">
      <div class="hero-image-wrap">
        ${imageData ? `<img class="hero-image" src="${imageData}" alt="Final rendered map">` : `<div class="empty-state">No final image captured.</div>`}
      </div>
      <div class="hero-meta">
        <div>
          <div class="eyebrow">Mythic Maps</div>
          <h1>2D Bearing Search Debug Summary</h1>
        </div>
        <div class="summary-grid">
          <div class="summary-item">
            <div class="summary-label">Coarse candidates</div>
            <div class="summary-value">${escapeHtml(String(coarseCandidates.length))}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">Rendered size</div>
            <div class="summary-value">${escapeHtml(`${options.width || defaultWidth} x ${options.height || defaultHeight}`)}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">Best bearing</div>
            <div class="summary-value">${escapeHtml(formatDebugNumber(bestCandidate?.bearing, 1))}&deg;</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">Best score</div>
            <div class="summary-value">${escapeHtml(formatDebugInteger(bestCandidate?.score))}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">Best footprint ratio</div>
            <div class="summary-value">${escapeHtml(formatDebugNumber(bestCandidate?.footprintRatio, 2))}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">Best center offset</div>
            <div class="summary-value">${escapeHtml(formatDebugNumber(bestCandidate?.centerDistanceNormalized, 3))}</div>
          </div>
        </div>
        ${searchConfigRows.length ? `
        <div class="config-list">
          ${searchConfigRows.map(([key, value]) => `
          <div class="config-row">
            <div class="config-key">${escapeHtml(key)}</div>
            <div class="config-value">${escapeHtml(value)}</div>
          </div>`).join("")}
        </div>` : `<div class="section-subtitle">Search config unavailable.</div>`}
      </div>
    </section>

    <section class="panel section">
      <div class="section-header">
        <div class="section-title">How The Score Works</div>
        <div class="section-subtitle">Pitch is always 0 in 2D. Score rewards filling the frame while staying centered and on-screen.</div>
      </div>
      <div class="explain-grid">
        <div class="explain-card">
          <div class="explain-title">Score Formula</div>
          <div class="config-list">
            ${scoreWeightRows.map(([key, value]) => `
            <div class="config-row">
              <div class="config-key">${escapeHtml(key)}</div>
              <div class="config-value">${escapeHtml(value)}</div>
            </div>`).join("")}
          </div>
        </div>
        <div class="explain-card">
          <div class="explain-title">Metric Definitions</div>
          <div class="config-list">
            ${metricExplainRows.map(([key, value]) => `
            <div class="config-row">
              <div class="config-key">${escapeHtml(key)}</div>
              <div class="config-value">${escapeHtml(value)}</div>
            </div>`).join("")}
          </div>
        </div>
      </div>
    </section>

    <section class="panel section">
      <div class="section-header">
        <div class="section-title">Coarse Search</div>
        <div class="section-subtitle">${escapeHtml(`${coarseCandidates.length} candidates`)}</div>
      </div>
      ${coarseCandidates.length
        ? `<div class="card-grid">${coarseCandidates.map((candidate) => buildCandidateCardHtml(candidate, bestCandidate)).join("")}</div>`
        : `<div class="empty-state">No coarse candidates captured.</div>`}
    </section>
  </div>
</body>
</html>`;
}

function buildCandidateCardHtml(candidate, bestCandidate) {
  const isBest = bestCandidate && candidate.id && candidate.id === bestCandidate.id;
  const cardClass = isBest ? "candidate-card candidate-card--best" : "candidate-card";
  return `
    <article class="${cardClass}">
      ${isBest ? `<div class="candidate-badge">Best bearing</div>` : ""}
      ${candidate.thumbnailDataUrl
        ? `<img class="candidate-image" src="${candidate.thumbnailDataUrl}" alt="candidate preview">`
        : `<div class="candidate-image candidate-image--empty">No preview</div>`}
      <div class="candidate-body">
        <div class="candidate-phase">coarse ${Number.isFinite(candidate.index) ? `${candidate.index}/${candidate.total || "?"}` : ""}</div>
        <div>Score: ${escapeHtml(formatDebugInteger(candidate.score))}</div>
        <div>Requested bearing: ${escapeHtml(formatDebugNumber(candidate.bearing, 1))}&deg;</div>
        <div>Rendered bearing: ${escapeHtml(formatDebugNumber(candidate.camera?.bearing, 1))}&deg;</div>
        <div>Capture zoom: ${escapeHtml(formatDebugZoom(candidate.requestedCaptureZoom ?? candidate.captureCamera?.zoom))}</div>
        <div>Display zoom: ${escapeHtml(formatDebugZoom(candidate.requestedZoom))} / rendered ${escapeHtml(formatDebugZoom(candidate.camera?.zoom))}</div>
        <div>Capture center: ${escapeHtml(formatDebugNumber(candidate.captureCamera?.center?.[0], 4))}, ${escapeHtml(formatDebugNumber(candidate.captureCamera?.center?.[1], 4))}</div>
        <div>Footprint: ${escapeHtml(formatDebugNumber(candidate.footprintRatio, 2))}</div>
        <div>Ratios: W ${escapeHtml(formatDebugNumber(candidate.bboxWidthRatio, 2))} / H ${escapeHtml(formatDebugNumber(candidate.bboxHeightRatio, 2))}</div>
        <div>Outside pad area: ${escapeHtml(formatDebugNumber(candidate.outsidePaddingAreaRatio, 2))}</div>
        <div>Center offset: ${escapeHtml(formatDebugNumber(candidate.centerDistanceNormalized, 3))}</div>
        <div>On-screen: ${escapeHtml(formatDebugNumber(candidate.onScreenRatio, 2))} / Inside pad: ${escapeHtml(formatDebugNumber(candidate.insidePaddingRatio, 2))}</div>
        <div>Area reward: ${escapeHtml(formatDebugInteger(candidate.footprintReward))}</div>
        <div>Center penalty: ${escapeHtml(formatDebugInteger(candidate.centerPenalty))}</div>
      </div>
    </article>`;
}

function formatDebugInteger(value) {
  return Number.isFinite(value) ? String(Math.round(value)) : "n/a";
}

function formatDebugNumber(value, digits = 2) {
  return Number.isFinite(value) ? Number(value).toFixed(digits) : "n/a";
}

function formatDebugZoom(value) {
  return Number.isFinite(value) ? Number(value).toFixed(2) : "n/a";
}

function escapeHtml(text) {
  return String(text)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;");
}

module.exports = {
  build2DMapDebugSummaryHtml,
};
